/**
 * The diverging colour ramp behind every percentile chip in the app.
 *
 * Previously this lived as 101 generated SCSS rules keyed off a `data-percentile`
 * attribute, which meant only the Explorer data grid could use it. It's a
 * function now so the Player Stats grid renders identical chips from the same
 * source, and so the toolbar legend can be built from the same endpoints it
 * colours cells with.
 *
 * The ramp is gamma-corrected (`|t| ** 0.78`) rather than linear: a linear mix
 * leaves the middle two thirds of the scale looking flat, so the 60th and 75th
 * percentiles read as the same colour. The exponent pushes separation out of the
 * extremes and into the range where most players actually sit.
 */

export type PercentileMode = "percentiles" | "ranks";

export interface PercentileColor {
  /** Chip background. */
  fill: string;
  /** Text colour. Constant — see `TEXT` for why. */
  text: string;
  /** `text-shadow` outlining `text` so it stays legible on the saturated ends of
   *  the ramp. */
  textOutline: string;
  /** A 30%-strength wash of `fill`, for shading a whole cell instead of a chip. */
  tint: string;
}

type Rgb = [number, number, number];

const NEUTRAL: Rgb = [236, 238, 241];
const WHITE: Rgb = [255, 255, 255];

/** Percentile mode reads cold-to-hot; rank mode keeps its own hues so the two
 *  are never mistaken for each other at a glance. */
const ENDPOINTS: Record<PercentileMode, { low: Rgb; high: Rgb }> = {
  percentiles: { low: [47, 120, 180], high: [199, 52, 28] },
  ranks: { low: [147, 129, 255], high: [33, 145, 140] },
};

const GAMMA = 0.78;

/**
 * Text stays one colour at every percentile, kept legible on the saturated ends
 * by an outline rather than by flipping to white. Flipping introduces a hard seam
 * partway up a continuous scale, and readers take that seam for a threshold in
 * the data — which it isn't.
 *
 * Four 1px offsets at **zero blur**, matching the Team Report data-table chip
 * (`TeamReport/TeamReportDataTable/TeamReportDataTableCell.tsx`). The zero blur
 * is the point: a blurred halo bleeds into the glyph and makes 11px bold numerals
 * look soft.
 *
 * Expressed as `text-shadow`, not `filter: drop-shadow` as the Team Report does.
 * They render identically on text, but `filter` promotes the element to its own
 * compositor layer — and with a chip in every cell that is hundreds of layers,
 * which can desynchronise from the sticky pinned columns during a horizontal
 * scroll and let cell content appear to slide over them.
 */
const TEXT = "rgba(0, 0, 0, 0.87)";
const OUTLINE_COLOR = "rgba(255, 255, 255, 0.4)";
const TEXT_OUTLINE = [
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
]
  .map(([x, y]) => `${x}px ${y}px 0 ${OUTLINE_COLOR}`)
  .join(", ");

const mix = (from: Rgb, to: Rgb, amount: number): Rgb => [
  Math.round(from[0] + (to[0] - from[0]) * amount),
  Math.round(from[1] + (to[1] - from[1]) * amount),
  Math.round(from[2] + (to[2] - from[2]) * amount),
];

const rgb = (color: Rgb) => `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

/**
 * @param percentile 0-100. Anything out of range is clamped; null/NaN reads as
 *   the neutral midpoint so a missing value never renders as "elite" or "awful".
 */
export function percentileColor(
  percentile: number | null | undefined,
  mode: PercentileMode = "percentiles",
): PercentileColor {
  const value =
    percentile == null || Number.isNaN(percentile)
      ? 50
      : Math.max(0, Math.min(100, percentile));

  const signed = (value - 50) / 50;
  const strength = Math.pow(Math.abs(signed), GAMMA);
  const endpoints = ENDPOINTS[mode];
  const end = signed < 0 ? endpoints.low : endpoints.high;

  return {
    fill: rgb(mix(NEUTRAL, end, strength)),
    text: TEXT,
    textOutline: TEXT_OUTLINE,
    tint: rgb(mix(WHITE, end, strength * 0.3)),
  };
}

/**
 * The legend swatch, sampled from `percentileColor` so it can't drift from the
 * cells it explains.
 *
 * `direction` is the CSS gradient direction, not a data direction: stops run
 * 0-100 percentile, so `to left` puts the *best* end on the left. Rank legends
 * want that, since they read "1st" on the left and "Last" on the right.
 */
export function percentileGradient(
  mode: PercentileMode = "percentiles",
  direction: "to right" | "to left" = "to right",
): string {
  const stops = [0, 10, 25, 40, 50, 60, 75, 90, 100].map(
    (p) => `${percentileColor(p, mode).fill} ${p}%`,
  );
  return `linear-gradient(${direction}, ${stops.join(", ")})`;
}

/** "1st", "2nd", "23rd" — used by rank chips. */
export function ordinalSuffix(n: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = n % 100;
  return suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
}
